import { Space, SpaceProps } from 'antd';
import { SpaceSize } from 'antd/lib/space';
import { FC } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { LinkButton, Button } from 'components/facade/button';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { BodyShort01, ProductiveHeading05 } from 'components/styles';

export const Image = styled.div<Pick<SpaceProps, 'size'>>`
  width: ${(props) =>
    props.size === 'large'
      ? '422px'
      : props.size === 'middle'
      ? '80px'
      : 'auto'};
  height: ${(props) =>
    props.size === 'large'
      ? '264px'
      : props.size === 'middle'
      ? '80px'
      : 'auto'};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  ${ProductiveHeading05};
`;

const Message = styled.div`
  ${BodyShort01};
  width: 80%;
`;

const PrimaryCTA = styled(Button).attrs({
  type: 'primary',
  iconPlacement: 'right',
})``;

const SecondaryCTA = styled(LinkButton)`
  display: flex;
  align-items: center;
  text-align: left;
`;

export const Content = styled(Space)``;
const Info = Content;
const CTA = Content;

interface ContainerProps {
  direction?: 'horizontal' | 'vertical';
  size?: 'small' | 'large' | 'middle' | [SpaceSize, SpaceSize];
}

/* ${Title}, ${Message} {
    width: 80%;
  } */

export const Container = styled(Space)<ContainerProps>`
  width: 424px;
  max-width: 424px;
  ${(props) =>
    props.direction === 'horizontal' &&
    `
      ${Content} {
        width: 100%;
        max-width: 100%;
      };

      ${
        props.size === 'large' &&
        `
        ${Content} {
          width: 288px;
        };
      `
      }
    `};
`;

const StyledCallToAction = styled(Space).attrs({
  direction: 'vertical',
  size: 16,
})``;

type CallToAction = typeof StyledCallToAction & {
  Primary: typeof PrimaryCTA;
  Secondary: typeof SecondaryCTA;
};

const CallToAction: CallToAction = StyledCallToAction as any;

CallToAction.Primary = PrimaryCTA;
CallToAction.Secondary = SecondaryCTA;

interface EmptyStateComponentProps
  extends ClassNameOnlyProps,
    Omit<SpaceProps, 'title'> {
  image?: React.ReactNode;
  title?: any;
  message?: React.ReactNode;
  primaryCTA?: React.ReactNode;
  secondaryCTA?: React.ReactNode;
}

const EmptyStateComponent: FC<
  React.PropsWithChildren<EmptyStateComponentProps>
> = (props) => {
  const {
    className,
    image,
    title,
    message,
    primaryCTA,
    secondaryCTA,
    direction = 'vertical',
    size = 'middle',
    children,
    ...rest
  } = props;

  return (
    <Container
      className={className}
      direction={direction}
      size={
        size as
          | 'small'
          | 'large'
          | 'middle'
          | [SpaceSize, SpaceSize]
          | undefined
      }
      {...rest}
    >
      {image && <Image size={size}>{image}</Image>}
      <Content
        direction="vertical"
        size={size === 'middle' && direction === 'horizontal' ? 8 : 24}
      >
        {(title || message) && (
          <Info direction="vertical">
            {title && <Title>{title}</Title>}
            {message && <Message>{message}</Message>}
          </Info>
        )}
        {(primaryCTA || secondaryCTA) && (
          <CTA direction="vertical" size={16}>
            {primaryCTA}
            {secondaryCTA}
          </CTA>
        )}
      </Content>
      {children}
    </Container>
  );
};

type EmptyState = typeof EmptyStateComponent & {
  Image: typeof Image;
  Title: typeof Title;
  Message: typeof Message;
  CallToAction: typeof CallToAction;
};

const EmptyState: EmptyState = EmptyStateComponent as any;

EmptyState.Image = Image;
EmptyState.Title = Title;
EmptyState.Message = Message;
EmptyState.CallToAction = CallToAction;

const EmptyStateContainer = styled.div<{
  $transparent?: boolean;
  $isNew?: boolean;
}>`
  background-color: ${(props) =>
    props.$transparent ? 'transparent' : 'var(--white-100)'};
  padding: 160px 128px;
  border-radius: ${(props) =>
    props.$isNew ? 'var(--border-radius-small)' : '0px'};
`;

export { EmptyState, EmptyStateContainer };
